// PrepaintAPI.js
'use strict'

/**
 * Uniform, small fetch wrapper with JSON / Blob helpers and URL builders
 * for the Prepaint flows.
 */

// ------------- low-level fetch helpers -------------

/**
 * Parse a JSON response and normalize non-2xx errors.
 * @param {Response} res - Fetch response.
 * @returns {Promise<any>} Parsed JSON or null for 204.
 */
async function handleJSON(res) {
    if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`)
        err.status = res.status
        try {
            const ct = res.headers.get('Content-Type') || ''
            if (ct.includes('application/json')) err.body = await res.json()
        } catch {}
        throw err
    }
    if (res.status === 204) return null
    return res.json()
}

/**
 * Parse a Blob response and normalize non-2xx errors.
 * @param {Response} res - Fetch response.
 * @returns {Promise<Blob>} Parsed blob.
 */
async function handleBlob(res) {
    if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`)
        err.status = res.status
        throw err
    }
    return res.blob()
}

/**
 * GET JSON helper with default headers + same-origin credentials.
 * @param {string} url - Request URL.
 * @returns {Promise<any>} Parsed JSON.
 */
function getJSON(url) {
    return fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
    }).then(handleJSON)
}

/**
 * GET image blob helper with same-origin credentials.
 * @param {string} url - Request URL.
 * @returns {Promise<Blob>} Image blob.
 */
function getBlob(url) {
    return fetch(url, {
        method: 'GET',
        headers: { Accept: 'image/*' },
        credentials: 'same-origin',
    }).then(handleBlob)
}

/**
 * Add a cache-busting param when requested.
 * @param {string} url - Base URL.
 * @param {boolean} bust - Whether to add the cache param.
 * @returns {string} URL with cache-busting query param.
 */
function withBust(url, bust) {
    if (!bust) return url
    const u = new URL(url, window.location.origin)
    u.searchParams.set('_', String(Date.now()))
    return u.pathname + u.search
}

/**
 * Convert a blob into an object URL.
 * @param {Blob} blob - Image blob.
 * @returns {string} Object URL.
 */
function toObjectURL(blob) {
    return URL.createObjectURL(blob)
}

// ------------- endpoints -------------

/**
 * Centralized API endpoints for albums/photos and assets.
 */
const paths = {
    // Collections / JSON
    albums: () => `/prepaint-albums`,
    albumPhotos: (albumId) => `/prepaint-albums/${albumId}`,
    photo: (photoId) => `/prepaint-photos/${photoId}`,
    photoSimple: (photoId, colorSampleId) => `/prepaint-photos/${photoId}/${colorSampleId}`,

    // Images
    albumThumb: (albumId) => `/prepaint-albums/${albumId}/thumb`,
    photoThumb: (photoId) => `/prepaint-photos/${photoId}/thumb`,
    photoOriginal: (photoId) => `/prepaint-photos/${photoId}/original`,
    photoMask: (photoId) => `/prepaint-photos/${photoId}/mask`,
    photoPreview: (photoId, colorSampleId) => `/prepaint-photos/${photoId}/${colorSampleId}/preview`,

    // Projects
    storeFromPrepaint: () => `/projects/store-from-prepaint-photo`,
}

// ------------- label helper -------------

/**
 * Resolve a display label for a prepaint album.
 * Priority:
 * 1) JS translation map from prepaint.php (normalized keys).
 * 2) API translations filtered by locale (nameTranslations[0]).
 * 3) Album name fallback (capitalized).
 *
 * @param {Object} album - Prepaint album resource.
 * @returns {string} Display label.
 */
function getAlbumLabel(album) {
    if (!album) return ''

    const translationsMap = window.PrepaintTranslations || {}
    const albumName = (album.name || '').trim()
    const normalizedKey = normalizePrepaintKey(albumName)
    const translated = normalizedKey ? translationsMap[normalizedKey] : ''
    if (translated) return translated

    const translations = album.nameTranslations || []
    const firstTranslation = translations[0]?.translation
    if (firstTranslation) return String(firstTranslation).trim()

    const fallback = albumName
    return fallback ? fallback.charAt(0).toUpperCase() + fallback.slice(1) : ''
}

/**
 * Normalize prepaint album names into translation keys.
 * Converts to lowercase and replaces any non-alphanumeric run with "_".
 * @param {string} value - Raw album name from the database.
 * @returns {string} Normalized key for translation lookup.
 */
function normalizePrepaintKey(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
}

// ------------- API -------------

const PrepaintAPI = {
    // ----- JSON: albums & photos -----

    /** @returns {Promise<Array>} PrepaintAlbumCollection (array of album resources) */
    listAlbums() {
        return getJSON(paths.albums())
    },

    /** @returns {Promise<Object>} PrepaintAlbumResource with `prepaintPhotos` */
    getAlbumPhotos(prepaint_album_id) {
        return getJSON(paths.albumPhotos(prepaint_album_id))
    },

    /** @returns {Promise<Object>} PrepaintPhotoResource (photo with color_samples for current app swatch group) */
    getPhoto(prepaint_photo_id) {
        return getJSON(paths.photo(prepaint_photo_id))
    },

    /** @returns {Promise<Object>} PrepaintPhotoResource (restricted to given colorSampleId if present on photo) */
    getPhotoSimple(prepaint_photo_id, color_sample_id) {
        return getJSON(paths.photoSimple(prepaint_photo_id, color_sample_id))
    },

    // ----- Images: URL builders (use directly in <img src>) -----

    /** @returns {string} Album thumbnail URL (optionally cache-busted). */
    albumThumbURL(prepaint_album_id, { bust = false } = {}) {
        return withBust(paths.albumThumb(prepaint_album_id), bust)
    },
    /** @returns {string} Photo thumbnail URL (optionally cache-busted). */
    photoThumbURL(prepaint_photo_id, { bust = false } = {}) {
        return withBust(paths.photoThumb(prepaint_photo_id), bust)
    },
    /** @returns {string} Photo original URL (optionally cache-busted). */
    photoOriginalURL(prepaint_photo_id, { bust = false } = {}) {
        return withBust(paths.photoOriginal(prepaint_photo_id), bust)
    },
    /** @returns {string} Photo mask URL (optionally cache-busted). */
    photoMaskURL(prepaint_photo_id, { bust = false } = {}) {
        return withBust(paths.photoMask(prepaint_photo_id), bust)
    },
    /** @returns {string} Photo preview URL (optionally cache-busted). */
    photoPreviewURL(prepaint_photo_id, color_sample_id, { bust = false } = {}) {
        return withBust(paths.photoPreview(prepaint_photo_id, color_sample_id), bust)
    },

    // ----- Images: Blob/ObjectURL helpers (optional prefetch/draw) -----

    /** @returns {Promise<Blob>} Album thumbnail blob. */
    fetchAlbumThumbBlob(prepaint_album_id) {
        return getBlob(paths.albumThumb(prepaint_album_id))
    },
    /** @returns {Promise<Blob>} Photo thumbnail blob. */
    fetchPhotoThumbBlob(prepaint_photo_id) {
        return getBlob(paths.photoThumb(prepaint_photo_id))
    },
    /** @returns {Promise<Blob>} Photo original blob. */
    fetchPhotoOriginalBlob(prepaint_photo_id) {
        return getBlob(paths.photoOriginal(prepaint_photo_id))
    },
    /** @returns {Promise<Blob>} Photo mask blob. */
    fetchPhotoMaskBlob(prepaint_photo_id) {
        return getBlob(paths.photoMask(prepaint_photo_id))
    },
    /** @returns {Promise<Blob>} Photo preview blob. */
    fetchPhotoPreviewBlob(prepaint_photo_id, color_sample_id) {
        return getBlob(paths.photoPreview(prepaint_photo_id, color_sample_id))
    },

    /** @returns {Promise<string>} Album thumb object URL. */
    albumThumbObjectURL: async (id) => toObjectURL(await getBlob(paths.albumThumb(id))),
    /** @returns {Promise<string>} Photo thumb object URL. */
    photoThumbObjectURL: async (id) => toObjectURL(await getBlob(paths.photoThumb(id))),
    /** @returns {Promise<string>} Photo original object URL. */
    photoOriginalObjectURL: async (id) => toObjectURL(await getBlob(paths.photoOriginal(id))),
    /** @returns {Promise<string>} Photo mask object URL. */
    photoMaskObjectURL: async (id) => toObjectURL(await getBlob(paths.photoMask(id))),
    /** @returns {Promise<string>} Photo preview object URL. */
    photoPreviewObjectURL: async (pid, csid) => toObjectURL(await getBlob(paths.photoPreview(pid, csid))),

    /**
     * Given a PrepaintPhotoResource, pick a default color_sample_id.
     * Current rule: first item if present.
     */
    pickDefaultColorSampleId(photo) {
        const arr = photo && Array.isArray(photo.color_samples) ? photo.color_samples : []
        return arr.length ? arr[0].id : null
    },

    getAlbumLabel,
}

export default PrepaintAPI
window.PrepaintAPI = PrepaintAPI
